// Module Vuex pour lot
import dexieService from '@/services/dexieService'

const state = {
  currentLot: null,
  lots: [],
  loading: false,
  error: null,
  notFoundError: false
}

const mutations = {
  SET_CURRENT_LOT(state, lot) {
    state.currentLot = lot
  },
  SET_LOTS(state, lots) {
    state.lots = lots
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_NOT_FOUND_ERROR(state, notFound) {
    state.notFoundError = notFound
  },
  UPDATE_LOT(state, updatedLot) {
    if (state.currentLot && state.currentLot.id === updatedLot.id) {
      state.currentLot = { ...state.currentLot, ...updatedLot }
    }
    
    const index = state.lots.findIndex(lot => lot.id === updatedLot.id)
    if (index !== -1) {
      state.lots.splice(index, 1, { ...state.lots[index], ...updatedLot })
    }
  },
  UPDATE_PLANIFIED_AT(state, planifiedAt) {
    if (state.currentLot) {
      state.currentLot.planifiedAt = planifiedAt
    }
  }
}

const actions = {
  async fetchLot({ commit }, lotId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    commit('SET_NOT_FOUND_ERROR', false)
    
    try {
      // Récupérer la propriété depuis IndexedDB via Dexie
      const property = await dexieService.getProperty(lotId)
      
      if (!property) {
        commit('SET_NOT_FOUND_ERROR', true)
        throw new Error(`Propriété avec l'ID ${lotId} introuvable dans IndexedDB`)
      }
      
      // Adapter la structure de données de la propriété au format attendu par le composant
      // En préservant TOUTES les données telles qu'elles sont stockées dans IndexedDB
      const lot = {
        // Préserver toutes les propriétés d'origine
        ...property,
        // S'assurer que les propriétés essentielles ont des valeurs par défaut
        id: property.id,
        nsil: property.nsil || '',
        propertyCode: property.propertyCode || '',
        buildingCode: property.buildingCode || '',
        unitCode: property.unitCode || '',
        // Gestionnaire technique - préserver la structure complète
        technicalManager: property.technicalManager || null,
        // Dates de visite - préserver les valeurs brutes pour le formatage ultérieur
        scheduledVisitAt: property.scheduledVisitAt || '',
        scheduledVisitStatus: property.scheduledVisitStatus || '',
        planifiedAt: property.planifiedAt || '',
        // Adresse
        address: property.address || {},
        // Type et occupation
        type: property.type || '',
        occupancy: property.occupancy || '',
        // Relations
        owner: property.owner || null,
        manager: property.manager || null,
        legalOwner: property.legalOwner || null,
        assistance: property.assistance || null,
        lastOccupant: property.lastOccupant || null,
        // Équipes - IMPORTANT pour calculer la délégation
        teams: property.teams || [],
        // Informations détaillées supplémentaires
        roomNumber: property.roomNumber || null,
        residence: property.residence || '',
        floor: property.floor || '',
        door: property.door || '',
        digicode: property.digicode || '',
        lat: property.lat || '',
        lng: property.lng || '',
        areaSize: property.areaSize || '',
        pdlGaz: property.pdlGaz || '',
        pdlElec: property.pdlElec || '',
        parentBatch: property.parentBatch || '',
        parentId: property.parentId || null,
        panels: property.panels || [],
        // Relation parent
        relatedParent: property.relatedParent || null
      }

      commit('SET_CURRENT_LOT', lot)
      return lot
    } catch (error) {
      console.error('Erreur lors de la récupération de la propriété depuis IndexedDB:', error)
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchLots({ commit }, filters = {}) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      // Récupérer toutes les propriétés depuis IndexedDB via Dexie
      const properties = await dexieService.filterProperties(filters)
      
      // Adapter au format attendu
      const lots = properties.map(property => ({
        id: property.id,
        nsil: property.nsil || '',
        propertyCode: property.propertyCode || '',
        buildingCode: property.buildingCode || '',
        type: property.type || '',
        occupancy: property.occupancy || '',
        address: property.address || {},
        technicalManager: property.technicalManager || null,
        scheduledVisitAt: property.scheduledVisitAt || '',
        scheduledVisitStatus: property.scheduledVisitStatus || '',
        planifiedAt: property.planifiedAt || '',
        ...property
      }))
      
      commit('SET_LOTS', lots)
      return lots
    } catch (error) {
      console.error('Erreur lors de la récupération des propriétés depuis IndexedDB:', error)
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateLot({ commit }, { lotId, updates }) {
    try {
      // Récupérer la propriété actuelle
      const currentProperty = await dexieService.getProperty(lotId)
      
      if (!currentProperty) {
        throw new Error(`Propriété avec l'ID ${lotId} introuvable`)
      }
      
      // Fusionner les mises à jour avec la propriété actuelle
      const updatedProperty = { ...currentProperty, ...updates }
      
      // Sauvegarder dans IndexedDB
      await dexieService.addProperty(updatedProperty)
      
      // Mettre à jour le store
      commit('UPDATE_LOT', updatedProperty)
      
      return updatedProperty
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la propriété:', error)
      commit('SET_ERROR', error.message)
      throw error
    }
  },

  async updatePlanifiedAt({ commit, state }, planifiedAt) {
    try {
      // Mettre à jour localement d'abord
      commit('UPDATE_PLANIFIED_AT', planifiedAt)
      
      // Sauvegarder dans IndexedDB si on a un lot courant
      if (state.currentLot && state.currentLot.id) {
        await dexieService.updatePropertyPlanification(state.currentLot.id, planifiedAt)
      }
      
      return planifiedAt
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la planification:', error)
      throw error
    }
  },

  setCurrentLot({ commit }, lot) {
    commit('SET_CURRENT_LOT', lot)
  },

  clearCurrentLot({ commit }) {
    commit('SET_CURRENT_LOT', null)
  }
}

const getters = {
  currentLot: state => state.currentLot,
  lots: state => state.lots,
  loading: state => state.loading,
  error: state => state.error,
  notFoundError: state => state.notFoundError,
  lotsByStatus: state => status => {
    return state.lots.filter(lot => lot.occupancy === status)
  },
  lotsByType: state => type => {
    return state.lots.filter(lot => lot.type === type)
  }
}

export default { 
  namespaced: true, 
  state, 
  mutations, 
  actions, 
  getters 
}
